import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid verification link. No token provided.');
      return;
    }

    const verifyEmail = async () => {
      try {
        await axios.post(`${API_URL}/api/auth/verify-email`, { token });
        setStatus('success');
        setTimeout(() => navigate('/dashboard'), 2000);
      } catch (err) {
        setStatus('error');
        setErrorMessage(
          err.response?.data?.detail || 'Verification failed. The link may be invalid or expired.'
        );
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage:
          'url(https://images.unsplash.com/photo-1737629918399-45526c4f9264?auto=format&fit=crop&w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/cf-logo.png" alt="CreatorFlow" className="w-12 h-12 rounded-xl" />
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="gradient-text">CreatorFlow</span>
            </h1>
          </div>
        </div>

        <Card className="bg-slate-900/90 border-slate-800 backdrop-blur-xl">
          {/* Loading */}
          {status === 'loading' && (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2 text-violet-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verifying Email
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300" data-testid="verify-email-loading">
                  Please wait while we verify your email address...
                </p>
              </CardContent>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  Email Verified
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-300" data-testid="verify-email-success">
                  Your email has been verified successfully. Redirecting to dashboard...
                </p>
                <Link to="/dashboard">
                  <Button className="w-full btn-gradient" data-testid="go-to-dashboard-btn">
                    Go to Dashboard
                  </Button>
                </Link>
              </CardContent>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2 text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  Verification Failed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-300" data-testid="verify-email-error">
                  {errorMessage}
                </p>
                <Link to="/">
                  <Button className="w-full btn-gradient" data-testid="back-to-login-btn">
                    Back to Sign In
                  </Button>
                </Link>
              </CardContent>
            </>
          )}
        </Card>

        <div className="text-center mt-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            data-testid="verify-email-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
